import {
  CountriesAndLanguagesProvider,
  CountriesAndLanguagesProviderProps,
  Country,
  CountryCode,
  Language,
} from "@made-people/grease-countries-and-languages";
import { PropsWithChildren, useCallback, useMemo } from "react";
import {
  FrontendContextV1,
  SiteResponseV1,
} from "@made-people/nestjs-bff-service";
import { useCentraSelectionResponse } from "@made-people/grease-centra";
import {
  SitesProvider,
  SitesProviderProps,
  SitesProviderSite,
} from "@made-people/grease-sites";
import {
  useGearsConfigurationVariable,
  useGearsServiceUrl,
} from "@made-people/gears-configuration";
import { BffClient } from "@made-people/gears-bff-client";
import { useHref } from "@made-people/grease-link";

export type MmCountriesAndLanguagesProviderProps = PropsWithChildren<{
  frontendContext: FrontendContextV1;
  sites: SiteResponseV1[];
  countriesAndLanguages: {
    countries: Country[];
    languages: Language[];
  };
}>;

export const MmCountriesAndLanguagesProvider = (
  props: MmCountriesAndLanguagesProviderProps,
) => {
  const selectionResponse = useCentraSelectionResponse();
  const bffUrl = useGearsServiceUrl("bff");
  const tenantId = useGearsConfigurationVariable("tenantId");
  const href = useHref();

  const updateCountryAndLanguage = useCallback(
    async (
      newCountryCode: CountryCode,
      newLanguageCode: string,
      fullPath: string,
    ) => {
      const newCountry = props.countriesAndLanguages.countries.find(
        (country) => country.countryCode === newCountryCode,
      );

      if (!newCountry) {
        return;
      }

      const bffClient = new BffClient({ baseUrl: bffUrl, tenantId });

      const languageCountryRedirectData = {
        fullSlug: fullPath,
        targetFrontendContext: {
          market: newCountry.marketId,
          pricelist: props.frontendContext.pricelist,
          currencyCode: props.frontendContext.currencyCode,
          country: newCountry.countryCode,
          language: newLanguageCode,
          siteSlug: props.frontendContext.siteSlug,
          baseUrl: props.frontendContext.baseUrl,
          currency: props.frontendContext.currency,
        },
      };

      const response = await bffClient.findAlternateLinkForSlug(
        languageCountryRedirectData,
      );

      if (typeof response?.frontendContext?.siteSlug === "string") {
        window.location.href = href(
          `/${response.frontendContext.siteSlug}${response.alternateSlug}`,
        );
      }
    },
    [
      props.countriesAndLanguages.countries,
      props.frontendContext,
      bffUrl,
      tenantId,
      href,
    ],
  );

  /**
   *
   * SitesProvider configuration
   *
   */
  const sitesProviderProps: SitesProviderProps = useMemo(
    () => ({
      sites: props.sites.reduce((acc: SitesProviderSite[], site) => {
        // Find existing site with matching country codes
        const existingSite = acc.find(
          (s) => s.countryCodes[0] === site.countryCodes[0],
        );

        if (existingSite) {
          // Add new siteSlugs to existing site
          existingSite.siteSlugs = [
            ...existingSite.siteSlugs,
            {
              languageCode: site.primaryLanguageCode,
              siteSlug: site.siteSlug,
            },
          ];
          return acc;
        }

        // Add new site
        return [
          ...acc,
          {
            countryCodes: site.countryCodes,
            currencyCode: site.currencyCode,
            siteSlugs: [
              {
                languageCode: site.primaryLanguageCode,
                siteSlug: site.siteSlug,
              },
            ],
          },
        ];
      }, []),
    }),
    [props.sites],
  );

  const currentCountryCode =
    selectionResponse?.selection?.address.country ??
    props.frontendContext.country;

  const countriesAndLanguagesProviderProps: CountriesAndLanguagesProviderProps =
    useMemo(
      () => ({
        currentCountryCode: currentCountryCode as CountryCode,
        currentLanguageCode: props.frontendContext.language,
        countries: props.countriesAndLanguages.countries.filter((country) =>
          sitesProviderProps.sites.some((site) =>
            site.countryCodes.includes(country.countryCode),
          ),
        ),
        languages: props.countriesAndLanguages.languages,
        updateCountryAndLanguage,
      }),
      [
        props.countriesAndLanguages.countries,
        props.countriesAndLanguages.languages,
        sitesProviderProps.sites,
        currentCountryCode,
        props.frontendContext.language,
        updateCountryAndLanguage,
      ],
    );

  return (
    <CountriesAndLanguagesProvider {...countriesAndLanguagesProviderProps}>
      <SitesProvider {...sitesProviderProps}>{props.children}</SitesProvider>
    </CountriesAndLanguagesProvider>
  );
};
